/**
 * Data Plane Connector Store
 *
 * Single Zustand store with slices for:
 * - Grid layout management
 * - Connection status and subscription telemetry
 *
 * Entity data used to live here as a generic `entities` slice; it has
 * moved to per-entity Zustand stores owned by
 * `@rapidsos/data-plane-entity-hooks/zustand`. Components read entity
 * data via the typed `entityHooks.<entity>.useLive()` /
 * `entityHooks.<entity>.useMutation()` hooks (see `../entities`) rather
 * than reaching into this store.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { createConnectionSlice } from './slices/connection';
import { createGridSlice } from './slices/grid';
import type { DataPlaneStore } from './types';

export const useDataPlaneStore = create<DataPlaneStore>()(
  devtools(
    (...args) => ({
      ...createGridSlice(...args),
      ...createConnectionSlice(...args),
    }),
    { name: 'DataPlaneStore' },
  ),
);

/**
 * Expose the host store globally for Module Federation remotes.
 * Remotes can `import('dataPlaneConnector/store')` to get
 * `useDataPlaneStore` directly, or grab the global from
 * `window.__DATA_PLANE_STORE__`.
 */
if (typeof window !== 'undefined') {
  (window as any).__DATA_PLANE_STORE__ = useDataPlaneStore;
}

export type { DataPlaneStore } from './types';
export type { GridItem, GridConfig, GridSlice } from './slices/grid';
export type {
  ConnectionStatus,
  ConnectionError,
  SubscriptionState,
  ConnectionSlice,
} from './slices/connection';
